import React, { useState, useRef, useCallback, useEffect } from 'react';
import { IconButton, Tooltip, CircularProgress, Box, Snackbar, Alert } from '@mui/material';
import { Mic as MicIcon, Stop as StopIcon, KeyboardVoice } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

const FloatingMic = () => {
    const [isRecording, setIsRecording] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [hasTarget, setHasTarget] = useState(false); // ¿Hay un campo listo para escribir?
    const [errorMsg, setErrorMsg] = useState('');
    
    const targetInputRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);

    // --- 1. RASTREADOR DE FOCO Y ESTADO VISUAL ---
    useEffect(() => {
        const handleFocus = (e) => {
            // Si el usuario hace click/tab en un campo de texto
            if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
                targetInputRef.current = e.target;
                setHasTarget(true); // Activa las ondas azules
            }
        };

        const handleBlur = (e) => {
            // Si el usuario sale del campo (click afuera), apagamos las ondas
            // Pequeño delay para que no parpadee si cambia de un input a otro rápido
            setTimeout(() => {
                if (document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
                    setHasTarget(false);
                }
            }, 200);
        };

        document.addEventListener('focus', handleFocus, true);
        document.addEventListener('blur', handleBlur, true);
        
        return () => {
            document.removeEventListener('focus', handleFocus, true);
            document.removeEventListener('blur', handleBlur, true);
        };
    }, []);

    // --- 2. INYECTOR DE TEXTO (Setter Nativo) ---
    const injectText = (text) => {
        const input = targetInputRef.current;
        if (!input) {
            setErrorMsg("⚠️ Haz clic dentro de un campo de texto primero.");
            return;
        }

        const start = input.selectionStart || input.value.length;
        const end = input.selectionEnd || input.value.length;
        const currentValue = input.value;
        const prefix = (currentValue && start > 0 && currentValue[start-1] !== ' ') ? ' ' : '';
        const newValue = currentValue.substring(0, start) + prefix + text + currentValue.substring(end);

        const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value").set;
        const nativeTextAreaSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value").set;
        const setter = input.tagName === 'TEXTAREA' ? nativeTextAreaSetter : nativeInputValueSetter;
        
        if (setter) setter.call(input, newValue);
        else input.value = newValue;

        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus(); // Mantiene el foco para seguir escribiendo
    };

    // --- 3. GRABACIÓN ---
    const startRecording = useCallback(async () => {
        // Doble verificación: ¿Tenemos objetivo?
        if (!targetInputRef.current && document.activeElement) {
             if (document.activeElement.tagName === 'INPUT' || document.activeElement.tagName === 'TEXTAREA') {
                targetInputRef.current = document.activeElement;
             }
        }

        if (!targetInputRef.current) {
            setErrorMsg("⚠️ Haz clic en un campo de texto para activar el micrófono.");
            return;
        }

        setIsRecording(true);
        audioChunksRef.current = [];
        
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            mediaRecorderRef.current = new MediaRecorder(stream);
            mediaRecorderRef.current.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
            mediaRecorderRef.current.onstop = async () => {
                stream.getTracks().forEach(track => track.stop());
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                await sendAudioToBackend(audioBlob);
            };
            mediaRecorderRef.current.start();
        } catch (error) {
            console.error(error);
            setIsRecording(false);
            setErrorMsg("No se pudo acceder al micrófono.");
        }
    }, []);

    const stopRecording = useCallback(() => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
            setIsProcessing(true);
        }
    }, [isRecording]);

    const sendAudioToBackend = async (audioBlob) => {
        try {
            const formData = new FormData();
            formData.append('audio', audioBlob, 'recording.webm');
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/speech/transcribe`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            const data = await response.json();
            if (data.success && data.text) injectText(data.text);
        } catch (error) { setErrorMsg("Error de conexión IA."); } 
        finally { setIsProcessing(false); }
    };

    // --- ESTILOS DINÁMICOS ---
    // Color: Rojo (Grabando) | Azul (Listo/Target) | Gris (Inactivo)
    const bgColor = isRecording ? '#d32f2f' : (hasTarget ? '#1976d2' : '#9e9e9e');
    
    // Animación: Pulso Rojo (Grabando) | Ondas Azules (Tiene Target) | Ninguna
    let animationStyles = {};
    if (isRecording) {
        animationStyles = {
            animation: 'pulse-record 1.5s infinite',
            '@keyframes pulse-record': {
                '0%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.7)' },
                '70%': { boxShadow: '0 0 0 15px rgba(211, 47, 47, 0)' },
                '100%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)' }
            }
        };
    } else if (hasTarget) {
        animationStyles = {
            animation: 'ripple-ready 2s infinite',
            '@keyframes ripple-ready': {
                '0%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.7)' },
                '70%': { boxShadow: '0 0 0 10px rgba(25, 118, 210, 0)' },
                '100%': { boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)' }
            }
        };
    }

    return (
        <>
            <Tooltip title={isRecording ? "Detener" : (hasTarget ? "Dictar en campo activo" : "Selecciona un campo para dictar")}>
                <Box
                    sx={{
                        position: 'fixed',
                        top: 45,        // ⬆️ CAMBIO: Más arriba (justo bajo la barra azul)
                        right: 35,      // ➡ CAMBIO: Un poquito más a la derecha
                        zIndex: 99999,
                    }}
                >
                    <IconButton
                        onMouseDown={(e) => e.preventDefault()} 
                        onClick={isRecording ? stopRecording : startRecording}
                        sx={{
                            bgcolor: bgColor,
                            color: 'white',
                            width: 36,  // 🔍 CAMBIO: Más pequeño (aprox 10mm)
                            height: 36, // 🔍 CAMBIO: Más pequeño
                            boxShadow: 3,
                            transition: 'all 0.3s ease',
                            '&:hover': {
                                bgcolor: isRecording ? '#b71c1c' : '#1565c0',
                                transform: 'scale(1.1)'
                            },
                            ...animationStyles
                        }}
                    >
                        {isProcessing ? (
                            <CircularProgress size={18} color="inherit" />
                        ) : isRecording ? (
                            <StopIcon sx={{ fontSize: 20 }} /> // Icono más pequeño
                        ) : (
                            <KeyboardVoice sx={{ fontSize: 20 }} /> // Icono más pequeño
                        )}
                    </IconButton>
                </Box>
            </Tooltip>

            <Snackbar 
                open={!!errorMsg} 
                autoHideDuration={3000} 
                onClose={() => setErrorMsg('')}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="warning" variant="filled">{errorMsg}</Alert>
            </Snackbar>
        </>
    );
};

export default FloatingMic;